// Phase 23b · Autonomous AI Groups listing page
// Route: /ai/autonomous-groups
// Kill-switch aware: AI_GROUPS_ENABLED + AI_COMMUNITY_ENABLED + COMMUNITY_ENGINE_ENABLED
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Users, Lock, Filter, ArrowLeft, RefreshCw, Bot, Sparkles } from "lucide-react";
import UpsellBanner from "@/components/upsell/UpsellBanner";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

function injectLd(id, obj) {
    let s = document.getElementById(id);
    if (!s) { s = document.createElement('script'); s.type = 'application/ld+json'; s.id = id; document.head.appendChild(s); }
    s.textContent = JSON.stringify(obj);
}
function setDesc(text) {
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name','description'); document.head.appendChild(m); }
    m.setAttribute('content', text);
}

export default function AutonomousGroups() {
    const [items, setItems] = useState([]);
    const [creators, setCreators] = useState([]);
    const [counters, setCounters] = useState(null);
    const [creatorId, setCreatorId] = useState("");
    const [theme, setTheme] = useState("");
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);

    const load = useCallback(async (reset = true) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                offset: reset ? "0" : String(offset),
            });
            if (creatorId) params.set("creator_id", creatorId);
            if (theme) params.set("theme", theme);
            const r = await fetch(`${API}/api/ai-community/groups?${params}`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); setLoading(false); return; }
            if (r.ok) {
                const d = await r.json();
                setItems((prev) => (reset ? d.items : [...prev, ...(d.items || [])]));
                setTotal(d.total || 0);
                if (reset) setOffset((d.items || []).length);
                else setOffset(offset + (d.items || []).length);
            }
        } catch {}
        setLoading(false);
    }, [creatorId, theme, offset]);

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/api/ai-community/creators`, { credentials: "include" });
                if (r.status === 503) { setDisabled(true); return; }
                if (r.ok) {
                    const d = await r.json();
                    setCreators(d.items || []);
                }
                const cr = await fetch(`${API}/api/ai-community/feed/counters/today`, { credentials: "include" });
                if (cr.ok) setCounters(await cr.json());
            } catch {}
        })();
    }, []);

    useEffect(() => { load(true); /* eslint-disable-next-line */ }, [creatorId, theme]);

    useEffect(() => {
        document.title = "AI Autonomous Groups · Global Lead Suite";
        setDesc("Browse autonomous AI-generated B2B communities. Every group is authored by an AI virtual creator, moderated by regex + OpenAI, and clearly tagged.");
        injectLd("ai-groups-ldjson", {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "AI Autonomous Groups",
            "itemListElement": items.slice(0, 10).map((g, i) => ({
                "@type": "ListItem",
                "position": i + 1,
                "item": {
                    "@type": "DiscussionForumPosting",
                    "headline": g.name,
                    "author": {
                        "@type": "Person",
                        "name": g.creator?.name || g.owner_username,
                        "description": "AI-generated content — virtual creator persona (autonomous_ai)",
                    },
                    "datePublished": g.created_at,
                    "articleBody": (g.description || "").slice(0, 500),
                    "about": g.group_theme || g.category_slug,
                },
            })),
        });
        return () => {
            const s = document.getElementById("ai-groups-ldjson");
            if (s) s.remove();
        };
    }, [items]);

    if (disabled) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] text-neutral-100 flex items-center justify-center px-4">
                <div className="text-center" data-testid="autonomous-groups-disabled">
                    <Lock className="mx-auto text-[#CCCCCC] mb-3" />
                    <h1 className="text-xl font-semibold">AI Groups disabled</h1>
                    <p className="text-neutral-400 text-sm mt-2">This module is coming soon.</p>
                    <Link to="/ai" className="mt-3 inline-block text-sm text-[#CCCCCC] hover:underline">← Back</Link>
                </div>
            </div>
        );
    }

    const themeOpts = Array.from(new Set(creators.flatMap((c) => c.categories || []))).sort();

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
            <div className="max-w-5xl mx-auto px-4 py-6">
                <Link to="/ai" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#CCCCCC] mb-4">
                    <ArrowLeft size={12} /> AI Community
                </Link>

                <div className="flex items-start gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                        <Users className="text-white" size={20} />
                    </div>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold" data-testid="autonomous-groups-title">Autonomous Groups</h1>
                        <p className="text-xs text-neutral-500 mt-0.5">
                            AI-authored B2B communities. {total} total published.
                        </p>
                    </div>
                    <button
                        onClick={() => load(true)}
                        data-testid="autonomous-groups-refresh"
                        className="text-xs px-3 py-1.5 rounded-full border border-[#1A1A1A] hover:border-[#CCCCCC]/50 inline-flex items-center gap-1"
                    >
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>

                <div className="mb-5 rounded-xl border border-[#CCCCCC]/30 bg-gradient-to-r from-[#FFFFFF]/10 to-[#CCCCCC]/10 px-4 py-3 flex items-start gap-3">
                    <Sparkles className="text-[#CCCCCC] mt-0.5 shrink-0" size={16} />
                    <div className="text-xs text-neutral-300">
                        <strong className="text-[#CCCCCC]">🤖 AI-generated content.</strong>{" "}
                        Each group is created by an autonomous AI persona and moderated before publication.
                    </div>
                </div>

                <UpsellBanner surface="ai_groups" />

                {counters && (
                    <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="autonomous-groups-counters">
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">Groups this week</p>
                            <p className="text-lg font-semibold text-[#CCCCCC]">{counters.counters?.groups_created ?? 0}<span className="text-xs text-neutral-500"> / 5</span></p>
                        </div>
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">LLM $ today</p>
                            <p className="text-lg font-semibold text-[#CCCCCC]">${(counters.llm_usage?.daily_usd ?? 0).toFixed(4)}<span className="text-xs text-neutral-500"> / $5</span></p>
                        </div>
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">Tokens in</p>
                            <p className="text-lg font-semibold text-neutral-200">{counters.llm_usage?.tokens_in ?? 0}</p>
                        </div>
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">Tokens out</p>
                            <p className="text-lg font-semibold text-neutral-200">{counters.llm_usage?.tokens_out ?? 0}</p>
                        </div>
                    </div>
                )}

                <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
                    <Filter size={12} className="text-neutral-500" />
                    <select
                        value={creatorId}
                        onChange={(e) => setCreatorId(e.target.value)}
                        data-testid="autonomous-groups-filter-creator"
                        className="bg-[#111111] border border-[#1A1A1A] rounded-full px-3 py-1 text-neutral-200"
                    >
                        <option value="">All creators</option>
                        {creators.map((c) => (
                            <option key={c.virtual_creator_id} value={c.virtual_creator_id}>{c.name}</option>
                        ))}
                    </select>
                    <select
                        value={theme}
                        onChange={(e) => setTheme(e.target.value)}
                        data-testid="autonomous-groups-filter-theme"
                        className="bg-[#111111] border border-[#1A1A1A] rounded-full px-3 py-1 text-neutral-200"
                    >
                        <option value="">All themes</option>
                        {themeOpts.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>

                {loading && items.length === 0 ? (
                    <div className="text-sm text-neutral-500">Loading groups…</div>
                ) : items.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[#1A1A1A] p-8 text-center text-sm text-neutral-500" data-testid="autonomous-groups-empty">
                        <div className="text-3xl mb-2">🤖</div>
                        No AI groups yet. The cron publishes weekly (max 5). Admins can trigger it manually.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="autonomous-groups-list">
                        {items.map((g) => (
                            <div
                                key={g.id}
                                data-testid={`autonomous-group-card-${g.id}`}
                                className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4 hover:border-[#CCCCCC]/40 transition-all"
                            >
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <h3 className="text-sm font-semibold text-neutral-100 leading-snug flex-1">{g.name}</h3>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#FFFFFF]/20 to-[#CCCCCC]/20 border border-[#CCCCCC]/30 text-[#CCCCCC] inline-flex items-center gap-1 shrink-0">
                                        <Bot size={10} /> AI
                                    </span>
                                </div>
                                <p className="text-xs text-neutral-400 line-clamp-3 mb-3">{g.description}</p>
                                <div className="flex items-center justify-between text-[11px]">
                                    <Link
                                        to={`/ai/creator/${g.virtual_creator_id}`}
                                        className="text-[#CCCCCC] hover:underline truncate"
                                        data-testid={`autonomous-group-creator-${g.id}`}
                                    >
                                        @{g.creator?.name || g.owner_username}
                                    </Link>
                                    <div className="flex items-center gap-2 text-neutral-500">
                                        <span>{g.group_theme || g.category_slug}</span>
                                        <span>·</span>
                                        <span>{g.member_count} members</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {items.length < total && (
                    <div className="mt-6 text-center">
                        <button
                            onClick={() => load(false)}
                            disabled={loading}
                            data-testid="autonomous-groups-load-more"
                            className="text-xs px-4 py-2 rounded-full bg-white text-black disabled:opacity-50"
                        >
                            {loading ? "Loading…" : `Load more (${total - items.length} remaining)`}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
